import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Base query with authentication
const baseQuery = fetchBaseQuery({
  baseUrl:
    process.env.REACT_APP_API_URL || "https://api.silverfortcompany.com/api",
  prepareHeaders: (headers, { getState }) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // Don't set Content-Type - let RTK Query handle it automatically
    // For JSON it will set application/json
    // For FormData it will set multipart/form-data with boundary
    headers.set("Accept", "application/json");
    return headers;
  },
});

// Base query with re-authentication
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // If we get a 401, logout the user
  if (result.error && result.error.status === 401) {
    localStorage.removeItem("user");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_data");
    window.location.href = "/login";
  }

  return result;
};

// Create the API service
export const apiService = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Auth",
    "HomepageSettings",
    "Services",
    "ServiceCategories",
    "SpecialOffers",
    "Testimonials",
    "Links",
    "Analytics",
  ],
  endpoints: (builder) => ({
    // Auth endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: ["Auth"],
    }),
    logout: builder.mutation({
      query: () => ({
        url: "/logout",
        method: "POST",
      }),
      invalidatesTags: ["Auth"],
    }),
    getUser: builder.query({
      query: () => "/user",
      providesTags: ["Auth"],
    }),

    // Homepage Settings
    getHomepageSettings: builder.query({
      query: () => "/homepage-settings",
      providesTags: ["HomepageSettings"],
    }),
    updateHomepageSettings: builder.mutation({
      query: (settings) => ({
        url: "/homepage-settings",
        method: "POST",
        body: settings,
      }),
      invalidatesTags: ["HomepageSettings"],
    }),

    // Services
    getServices: builder.query({
      query: () => "/services",
      providesTags: ["Services"],
    }),
    getService: builder.query({
      query: (id) => `/services/${id}`,
      providesTags: (result, error, id) => [{ type: "Services", id }],
    }),
    searchServices: builder.query({
      query: (searchQuery) =>
        `/services?search=${encodeURIComponent(searchQuery)}`,
      providesTags: ["Services"],
    }),
    createService: builder.mutation({
      query: (body) => ({
        url: "/services/create",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Services"],
    }),
    updateService: builder.mutation({
      query: ({ id, body }) => ({
        url: `/services/update/${id}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Services"],
    }),
    deleteService: builder.mutation({
      query: (id) => ({
        url: `/services/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Services"],
    }),

    // Service Categories
    getServiceCategories: builder.query({
      query: () => "/services-categories",
      providesTags: ["ServiceCategories"],
    }),
    getServiceCategory: builder.query({
      query: (id) => `/services-categories/${id}`,
      providesTags: (result, error, id) => [{ type: "ServiceCategories", id }],
    }),
    createServiceCategory: builder.mutation({
      query: (body) => ({
        url: "/services-categories/create",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["ServiceCategories"],
    }),
    updateServiceCategory: builder.mutation({
      query: ({ id, body }) => ({
        url: `/services-categories/update/${id}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["ServiceCategories"],
    }),
    deleteServiceCategory: builder.mutation({
      query: (id) => ({
        url: `/services-categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ServiceCategories"],
    }),

    // Special Offers
    getSpecialOffers: builder.query({
      query: () => "/special-offers",
      providesTags: ["SpecialOffers"],
    }),
    searchOffers: builder.query({
      query: (searchQuery) =>
        `/special-offers?search=${encodeURIComponent(searchQuery)}`,
      providesTags: ["SpecialOffers"],
    }),
    getSpecialOffer: builder.query({
      query: (id) => `/special-offers/${id}`,
      providesTags: (result, error, id) => [{ type: "SpecialOffers", id }],
    }),
    createSpecialOffer: builder.mutation({
      query: (body) => ({
        url: "/special-offers/create",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["SpecialOffers"],
    }),
    updateSpecialOffer: builder.mutation({
      query: ({ id, body }) => ({
        url: `/special-offers/update/${id}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["SpecialOffers"],
    }),
    deleteSpecialOffer: builder.mutation({
      query: (id) => ({
        url: `/special-offers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SpecialOffers"],
    }),

    // Testimonials
    getTestimonials: builder.query({
      query: () => "/testimonials/public",
      providesTags: ["Testimonials"],
    }),
    getAdminTestimonials: builder.query({
      query: () => "/testimonials",
      providesTags: ["Testimonials"],
    }),
    getTestimonial: builder.query({
      query: (id) => `/testimonials/${id}`,
      providesTags: (result, error, id) => [{ type: "Testimonials", id }],
    }),
    createTestimonial: builder.mutation({
      query: (body) => ({
        url: "/testimonials",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Testimonials"],
    }),
    updateTestimonial: builder.mutation({
      query: ({ id, body }) => {
        // For FormData, add _method field for Laravel method spoofing
        if (body instanceof FormData) {
          body.append("_method", "PUT");
          return {
            url: `/testimonials/${id}`,
            method: "POST",
            body: body,
          };
        }
        // For JSON data, use PUT directly
        return {
          url: `/testimonials/${id}`,
          method: "PUT",
          body: body,
        };
      },
      invalidatesTags: ["Testimonials"],
    }),
    deleteTestimonial: builder.mutation({
      query: (id) => ({
        url: `/testimonials/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Testimonials"],
    }),

    // Links
    getLinks: builder.query({
      query: () => "/links",
      providesTags: ["Links"],
    }),
    getLink: builder.query({
      query: (id) => `/links/${id}`,
      providesTags: (result, error, id) => [{ type: "Links", id }],
    }),
    getLinksServices: builder.query({
      query: () => "/links/services/list",
      providesTags: ["Services"],
    }),
    getLinksOffers: builder.query({
      query: () => "/links/offers/list",
      providesTags: ["SpecialOffers"],
    }),
    getLinksAnalytics: builder.query({
      query: () => "/links/analytics",
      providesTags: ["Analytics"],
    }),
    createLink: builder.mutation({
      query: (body) => ({
        url: "/links",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Links", "Analytics"],
    }),
    updateLink: builder.mutation({
      query: ({ id, body }) => {
        // For FormData, add _method field for Laravel method spoofing
        if (body instanceof FormData) {
          body.append("_method", "PUT");
          return {
            url: `/links/${id}`,
            method: "POST",
            body: body,
          };
        }
        // For JSON data, use PUT directly
        return {
          url: `/links/${id}`,
          method: "PUT",
          body: body,
        };
      },
      invalidatesTags: ["Links", "Analytics"],
    }),
    deleteLink: builder.mutation({
      query: (id) => ({
        url: `/links/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Links", "Analytics"],
    }),

    // Analytics
    getAnalytics: builder.query({
      query: () => "/analytics/dashboard-summary",
      providesTags: ["Analytics"],
    }),
    getDetailedAnalytics: builder.query({
      query: () => "/analytics/detailed",
      providesTags: ["Analytics"],
    }),
    getChartData: builder.query({
      query: () => "/analytics/chart-data",
      providesTags: ["Analytics"],
    }),
    getVisitors: builder.query({
      query: ({ type = "today", page = 1, per_page = 20 } = {}) =>
        `/analytics/visitors?type=${type}&page=${page}&per_page=${per_page}`,
      providesTags: ["Analytics"],
    }),
    trackVisit: builder.mutation({
      query: (body) => ({
        url: "/analytics/track",
        method: "POST",
        body: body,
      }),
    }),

    // Contact
    submitContactForm: builder.mutation({
      query: (formData) => ({
        url: "/contact",
        method: "POST",
        body: formData,
      }),
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Auth
  useLoginMutation,
  useLogoutMutation,
  useGetUserQuery,

  // Homepage Settings
  useGetHomepageSettingsQuery,
  useUpdateHomepageSettingsMutation,

  // Services
  useGetServicesQuery,
  useGetServiceQuery,
  useSearchServicesQuery,
  useLazySearchServicesQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,

  // Service Categories
  useGetServiceCategoriesQuery,
  useGetServiceCategoryQuery,
  useCreateServiceCategoryMutation,
  useUpdateServiceCategoryMutation,
  useDeleteServiceCategoryMutation,

  // Special Offers
  useGetSpecialOffersQuery,
  useGetSpecialOfferQuery,
  useCreateSpecialOfferMutation,
  useUpdateSpecialOfferMutation,
  useDeleteSpecialOfferMutation,
  useLazySearchOffersQuery,

  // Testimonials
  useGetTestimonialsQuery,
  useGetAdminTestimonialsQuery,
  useGetTestimonialQuery,
  useCreateTestimonialMutation,
  useUpdateTestimonialMutation,
  useDeleteTestimonialMutation,

  // Links
  useGetLinksQuery,
  useGetLinkQuery,
  useGetLinksServicesQuery,
  useGetLinksOffersQuery,
  useGetLinksAnalyticsQuery,
  useCreateLinkMutation,
  useUpdateLinkMutation,
  useDeleteLinkMutation,

  // Analytics
  useGetAnalyticsQuery,
  useGetDetailedAnalyticsQuery,
  useGetChartDataQuery,
  useGetVisitorsQuery,

  // Contact
  useSubmitContactFormMutation,
} = apiService;
